// Analytics of the Marketing module (pure). content_metrics rows are WEEKLY:
// each row is what happened that week, so every total is a SUM of rows.
// Piece rows count for the piece's channel; channel rows (no piece) for
// their own channel.
import { addDays, differenceInCalendarWeeks, getISODay } from "date-fns";
import type { Identifier } from "ra-core";

import { weekStartOf } from "@/components/market-intel/dates";

import { dayOf, parseDay, pieceDate } from "./calendar";
import type {
  ContentFormat,
  ContentItem,
  ContentMetric,
  MarketingChannel,
  MetricField,
} from "./types";
import { METRIC_FIELDS } from "./types";

export type AnalyticsPeriod = "30" | "90" | "all";

export const ANALYTICS_PERIODS: readonly AnalyticsPeriod[] = [
  "30",
  "90",
  "all",
];

export const isAnalyticsPeriod = (value: unknown): value is AnalyticsPeriod =>
  ANALYTICS_PERIODS.includes(value as AnalyticsPeriod);

export type MetricTotals = Record<MetricField, number>;

export const emptyTotals = (): MetricTotals =>
  Object.fromEntries(METRIC_FIELDS.map((field) => [field, 0])) as MetricTotals;

/** Sum of weekly rows (null counts as nothing). */
export const sumMetrics = (rows: readonly ContentMetric[]): MetricTotals => {
  const totals = emptyTotals();
  for (const row of rows)
    for (const field of METRIC_FIELDS) {
      const value = Number(row[field] ?? 0);
      if (Number.isFinite(value)) totals[field] += value;
    }
  return totals;
};

/** Likes + comments + shares (envíos) + saves (guardados). */
export const interactionsOf = (totals: MetricTotals): number =>
  totals.likes + totals.comments + totals.shares + totals.saves;

/**
 * Engagement rate = interactions / reach (impressions when there is no
 * reach), as a fraction (0.05 = 5 %). Null without an audience figure.
 */
export const engagementRate = (totals: MetricTotals): number | null => {
  const audience = totals.reach || totals.impressions;
  return audience > 0 ? interactionsOf(totals) / audience : null;
};

/** First Monday counted by a period ending at `today` (null = everything). */
export const periodStartWeek = (
  period: AnalyticsPeriod,
  today: string,
): string | null =>
  period === "all"
    ? null
    : weekStartOf(dayOf(addDays(parseDay(today), -Number(period) + 1)));

/** The channel a metrics row counts for. */
export const rowChannelId = (
  row: ContentMetric,
  itemsById: ReadonlyMap<string, ContentItem>,
): Identifier | null =>
  row.content_item_id != null
    ? (itemsById.get(String(row.content_item_id))?.channel_id ??
      row.channel_id ??
      null)
    : (row.channel_id ?? null);

export const indexById = <T extends { id: Identifier }>(
  rows: readonly T[],
): Map<string, T> => new Map(rows.map((row) => [String(row.id), row]));

export interface AnalyticsFilter {
  startWeek: string | null;
  channel?: string | null;
}

/** Rows of the period (by week_start) and of the channel filter. */
export const filterMetrics = (
  metrics: readonly ContentMetric[],
  itemsById: ReadonlyMap<string, ContentItem>,
  { startWeek, channel }: AnalyticsFilter,
): ContentMetric[] =>
  metrics.filter(
    (row) =>
      (!startWeek || row.week_start >= startWeek) &&
      (!channel ||
        String(rowChannelId(row, itemsById) ?? "") === String(channel)),
  );

export interface PieceStats {
  item: ContentItem;
  totals: MetricTotals;
  interactions: number;
  rate: number | null;
}

/** Totals of every piece that has rows in `rows`. */
export const pieceStats = (
  rows: readonly ContentMetric[],
  itemsById: ReadonlyMap<string, ContentItem>,
): PieceStats[] => {
  const grouped = new Map<string, ContentMetric[]>();
  for (const row of rows) {
    if (row.content_item_id == null) continue;
    const key = String(row.content_item_id);
    if (!itemsById.has(key)) continue;
    grouped.set(key, [...(grouped.get(key) ?? []), row]);
  }
  return [...grouped.entries()].map(([key, list]) => {
    const totals = sumMetrics(list);
    return {
      item: itemsById.get(key)!,
      totals,
      interactions: interactionsOf(totals),
      rate: engagementRate(totals),
    };
  });
};

export type TopBy = "engagement" | "leads";

/** Best pieces by interactions or by leads (ties: more reach first). */
export const topPieces = (
  stats: readonly PieceStats[],
  by: TopBy,
  limit = 5,
): PieceStats[] =>
  [...stats]
    .filter(
      (stat) => (by === "leads" ? stat.totals.leads : stat.interactions) > 0,
    )
    .sort(
      (left, right) =>
        (by === "leads"
          ? right.totals.leads - left.totals.leads
          : right.interactions - left.interactions) ||
        right.totals.reach - left.totals.reach,
    )
    .slice(0, limit);

export interface GroupStats<K> {
  key: K;
  totals: MetricTotals;
  interactions: number;
  rate: number | null;
  /** Pieces with metrics in the group. */
  pieces: number;
}

const groupStats = <K>(
  rows: readonly ContentMetric[],
  keyOf: (row: ContentMetric) => K | null,
): GroupStats<K>[] => {
  const groups = new Map<K, { rows: ContentMetric[]; pieces: Set<string> }>();
  for (const row of rows) {
    const key = keyOf(row);
    if (key == null) continue;
    const group = groups.get(key) ?? { rows: [], pieces: new Set<string>() };
    group.rows.push(row);
    if (row.content_item_id != null)
      group.pieces.add(String(row.content_item_id));
    groups.set(key, group);
  }
  return [...groups.entries()]
    .map(([key, group]) => {
      const totals = sumMetrics(group.rows);
      return {
        key,
        totals,
        interactions: interactionsOf(totals),
        rate: engagementRate(totals),
        pieces: group.pieces.size,
      };
    })
    .sort((left, right) => right.interactions - left.interactions);
};

/** Performance per channel (piece rows count for the piece's channel). */
export const statsByChannel = (
  rows: readonly ContentMetric[],
  itemsById: ReadonlyMap<string, ContentItem>,
): GroupStats<string>[] =>
  groupStats(rows, (row) => {
    const id = rowChannelId(row, itemsById);
    return id == null ? null : String(id);
  });

/** Performance per format (piece rows only). */
export const statsByFormat = (
  rows: readonly ContentMetric[],
  itemsById: ReadonlyMap<string, ContentItem>,
): GroupStats<ContentFormat>[] =>
  groupStats(rows, (row) =>
    row.content_item_id == null
      ? null
      : (itemsById.get(String(row.content_item_id))?.format ?? null),
  );

export interface WeekPoint {
  week: string;
  saves: number;
  shares: number;
  direct_messages: number;
}

/**
 * Weekly evolution of the three key numbers (guardados, envíos, mensajes
 * directos), oldest first, with empty weeks between the first and the last
 * filled in with zeros.
 */
export const weeklyKeySeries = (
  rows: readonly ContentMetric[],
): WeekPoint[] => {
  if (!rows.length) return [];
  const byWeek = new Map<string, WeekPoint>();
  for (const row of rows) {
    const week = weekStartOf(row.week_start);
    const point = byWeek.get(week) ?? {
      week,
      saves: 0,
      shares: 0,
      direct_messages: 0,
    };
    point.saves += Number(row.saves ?? 0);
    point.shares += Number(row.shares ?? 0);
    point.direct_messages += Number(row.direct_messages ?? 0);
    byWeek.set(week, point);
  }
  const weeks = [...byWeek.keys()].sort();
  const first = parseDay(weeks[0]);
  const count = differenceInCalendarWeeks(parseDay(weeks.at(-1)!), first) + 1;
  return Array.from({ length: count }, (_, index) => {
    const week = dayOf(addDays(first, index * 7));
    return (
      byWeek.get(week) ?? { week, saves: 0, shares: 0, direct_messages: 0 }
    );
  });
};

export interface Insight {
  id: string;
  /** i18n key of the sentence. */
  key: string;
  args: Record<string, string | number>;
}

interface InsightInput {
  stats: readonly PieceStats[];
  byChannel: readonly GroupStats<string>[];
  byFormat: readonly GroupStats<ContentFormat>[];
  series: readonly WeekPoint[];
  channels: readonly MarketingChannel[];
  /** Translates a format / weekday into words. */
  formatLabel: (format: ContentFormat) => string;
  weekdayLabel: (isoDay: number) => string;
}

const best = <T>(list: readonly T[], score: (entry: T) => number): T | null =>
  list.reduce<T | null>(
    (top, entry) => (top == null || score(entry) > score(top) ? entry : top),
    null,
  );

const percent = (value: number): number => Math.round(value * 1000) / 10;

/**
 * "Qué funciona mejor": a few plain sentences computed from the data. Groups
 * need at least two pieces so one lucky post does not make a rule.
 */
export const buildInsights = ({
  stats,
  byChannel,
  byFormat,
  series,
  channels,
  formatLabel,
  weekdayLabel,
}: InsightInput): Insight[] => {
  const insights: Insight[] = [];
  const formats = byFormat.filter(
    (group) => group.pieces >= 2 && group.rate != null,
  );
  const topFormat = best(formats, (group) => group.rate ?? 0);
  if (topFormat && formats.length > 1)
    insights.push({
      id: "format",
      key: "marketing.insights.best_format",
      args: {
        format: formatLabel(topFormat.key),
        rate: percent(topFormat.rate ?? 0),
      },
    });

  const usefulFormat = best(
    byFormat.filter((group) => group.pieces >= 2),
    (group) => (group.totals.saves + group.totals.shares) / group.pieces,
  );
  if (
    usefulFormat &&
    usefulFormat.totals.saves + usefulFormat.totals.shares > 0
  )
    insights.push({
      id: "useful",
      key: "marketing.insights.most_useful_format",
      args: {
        format: formatLabel(usefulFormat.key),
        count: Math.round(
          (usefulFormat.totals.saves + usefulFormat.totals.shares) /
            usefulFormat.pieces,
        ),
      },
    });

  const rated = byChannel.filter((group) => group.rate != null);
  const topChannel = best(rated, (group) => group.rate ?? 0);
  const channelName = (id: string) =>
    channels.find((channel) => String(channel.id) === id)?.name ?? id;
  if (topChannel && rated.length > 1)
    insights.push({
      id: "channel",
      key: "marketing.insights.best_channel",
      args: {
        channel: channelName(topChannel.key),
        rate: percent(topChannel.rate ?? 0),
      },
    });

  const dmChannel = best(byChannel, (group) => group.totals.direct_messages);
  if (dmChannel && dmChannel.totals.direct_messages > 0)
    insights.push({
      id: "dms",
      key: "marketing.insights.most_dms",
      args: {
        channel: channelName(dmChannel.key),
        count: dmChannel.totals.direct_messages,
      },
    });

  const weekdays = new Map<number, { interactions: number; pieces: number }>();
  for (const stat of stats) {
    const date = pieceDate(stat.item);
    if (!date) continue;
    const day = getISODay(date);
    const entry = weekdays.get(day) ?? { interactions: 0, pieces: 0 };
    entry.interactions += stat.interactions;
    entry.pieces += 1;
    weekdays.set(day, entry);
  }
  const days = [...weekdays.entries()].filter(([, entry]) => entry.pieces >= 2);
  const topDay = best(days, ([, entry]) => entry.interactions / entry.pieces);
  if (topDay && days.length > 1)
    insights.push({
      id: "weekday",
      key: "marketing.insights.best_weekday",
      args: {
        day: weekdayLabel(topDay[0]),
        count: Math.round(topDay[1].interactions / topDay[1].pieces),
      },
    });

  const leader = best(stats, (stat) => stat.totals.leads);
  if (leader && leader.totals.leads > 0)
    insights.push({
      id: "leads",
      key: "marketing.insights.top_leads",
      args: { title: leader.item.title, count: leader.totals.leads },
    });

  const filled = series.filter(
    (point) => point.saves + point.shares + point.direct_messages > 0,
  );
  if (filled.length >= 2) {
    const [previous, last] = filled.slice(-2);
    const now = last.saves + last.shares;
    const before = previous.saves + previous.shares;
    if (before > 0 && now !== before)
      insights.push({
        id: "trend",
        key:
          now > before
            ? "marketing.insights.trend_up"
            : "marketing.insights.trend_down",
        args: { change: Math.abs(Math.round(((now - before) / before) * 100)) },
      });
  }
  return insights;
};
